/**
 * The wallet is on a network the venue does not settle on, and a button that
 * fixes it.
 *
 * The button names the network it switches to, and it is the only thing that
 * switches one: `<WalletProvider>`'s `ensureVenueChain` runs on a click and never
 * on its own. While the wallet is on the wrong network its balances cannot be
 * read and nothing it signs would land, so a panel shows this *instead of* a
 * disabled form. Once the switch lands, the account watcher re-reads and the
 * panel carries on from where the user was.
 *
 * `docs/07` §7 owns the wording.
 */

import { useState } from 'react'
import { Button } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { chainName } from '@/domain/chain-trust'
import { useWallet } from './WalletProvider'

/**
 * The notice wired to `useWallet()`, for `/money`'s panels: shown while the
 * wallet is on another network, with the switch button and the reason a
 * switch did not happen.
 */
export function WalletWrongChainNotice({ className }: { className?: string }) {
  const wallet = useWallet()
  const [failed, setFailed] = useState<string | null>(null)
  if (!wallet.chainMismatch) return null
  return (
    <WrongChainNotice
      mismatch={wallet.chainMismatch}
      busy={wallet.busy}
      onSwitch={() => void wallet.switchToVenueChain().then(setFailed)}
      error={failed}
      {...(className ? { className } : {})}
    />
  )
}

export interface WrongChainNoticeProps {
  mismatch: { wallet: number; venue: number }
  /** Omitted where the screen's own primary button does the switch. */
  onSwitch?: () => void
  busy?: boolean
  /** Why the last switch did not happen. */
  error?: string | null
  className?: string
}

export function WrongChainNotice({ mismatch, onSwitch, busy = false, error = null, className }: WrongChainNoticeProps) {
  const venue = chainName(mismatch.venue)
  return (
    <div
      role="status"
      data-testid="wrong-chain"
      className={cx(
        'flex flex-col gap-2 rounded-sm bg-warning-bg px-2 py-1_5 text-caption text-warning-text',
        className,
      )}
    >
      <p>
        Your wallet is on {chainName(mismatch.wallet)}. UpTik settles on {venue}.
      </p>
      {onSwitch ? (
        <div>
          <Button variant="primary" size="sm" loading={busy} onClick={onSwitch}>
            Switch to {venue}
          </Button>
        </div>
      ) : null}
      {error ? <p className="text-danger-text">{error}</p> : null}
    </div>
  )
}
